import React, {
  forwardRef,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  Heading,
  HStack,
  Image,
  Pressable,
  Stack,
  Text,
  VStack,
} from "native-base";
import Autocomplete from "react-google-autocomplete";
import DatePicker from "react-datepicker";
import moment from "moment";
import { MdDeleteForever, MdOutlineClear, MdMyLocation } from "react-icons/md";
import { Marker } from "@react-google-maps/api";
import styles from "./styles.module.css";
import locationIcon from "../../assets/icons/location.svg";
import calendar from "../../assets/icons/calendar.svg";
import clock from "../../assets/icons/clock.svg";
import colors from "../../constants/colors";
import { formatLocationResult } from "../../utils";
import Cell from "../cell";
import { formatDuration } from "../../utils";
import constants from "../../constants";
import Modal from "../modal";
import Map from "../map";
import classNames from "classnames";
import { StatusContext } from "../../contexts";

const CustomDatePickerInput = forwardRef(
  ({ value, onClick, disabled, readOnly }, ref) => (
    <Pressable
      ref={ref}
      onPress={onClick}
      disabled={disabled}
      style={{ cursor: readOnly ? "default" : "pointer" }}>
      <HStack
        borderColor={
          disabled || readOnly ? colors.transparent : colors.secondary
        }
        borderRadius={5}
        borderWidth={1}
        padding={3}
        space={2}
        minH={50}>
        <Image src={calendar} alt="calendar" width={5} height={5} />
        <Text numberOfLines={1}>
          {value && !disabled ? value : "----------"}
        </Text>
      </HStack>
    </Pressable>
  ),
);

CustomDatePickerInput.displayName = "CustomDatePickerInput";

CustomDatePickerInput.propTypes = {
  onClick: PropTypes.func,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
};

function CustomDatePicker(props) {
  return (
    <DatePicker
      {...props}
      minDate={moment().add(props.minDays, "days").toDate()}
      dateFormat="yyyy-MM-dd"
      customInput={
        <CustomDatePickerInput
          disabled={props.disabled}
          readOnly={props.readOnly}
        />
      }
    />
  );
}

CustomDatePicker.propTypes = {
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  minDays: PropTypes.number,
};

const CustomTimePickerInput = forwardRef(
  ({ value, onClick, disabled, readOnly }, ref) => (
    <Pressable
      ref={ref}
      onPress={onClick}
      disabled={disabled}
      style={{ cursor: readOnly ? "default" : "pointer" }}>
      <HStack
        borderColor={
          disabled || readOnly ? colors.transparent : colors.secondary
        }
        borderRadius={5}
        borderWidth={1}
        padding={3}
        space={2}
        minH={50}>
        <Image src={clock} alt="clock" width={5} height={5} />
        <Text numberOfLines={1}>
          {value && !disabled ? moment(value).format("HH:mm") : "--:-- --"}
        </Text>
      </HStack>
    </Pressable>
  ),
);

CustomTimePickerInput.displayName = "CustomTimePickerInput";

CustomTimePickerInput.propTypes = {
  onClick: PropTypes.func,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
};

function CustomTimePicker(props) {
  return (
    <DatePicker
      {...props}
      showTimeSelect
      showTimeSelectOnly
      timeIntervals={5}
      dateFormat="yyyy-MM-dd HH:mm"
      customInput={
        <CustomTimePickerInput
          disabled={props.disabled}
          readOnly={props.readOnly}
        />
      }
    />
  );
}

CustomTimePicker.propTypes = {
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
};

function getStay(arrivalDate, departureDate, hideStay) {
  if (!arrivalDate || !departureDate || hideStay) {
    return "0d 0h 0m";
  }
  return formatDuration(
    moment(departureDate).diff(arrivalDate, "seconds"),
    false,
  );
}

function Location(props) {
  const {
    title,
    numeral,
    location,
    onChange,
    isLast,
    hideArrivalDate,
    hideDepartureDate,
    hideStay,
    readOnly,
    disabled,
    readOnlyDepartureDate,
    onDelete,
    clearDisabled,
    readOnlyObservation,
    forceDepartureDate,
    minDays,
  } = props;
  const status = useContext(StatusContext);
  const autocompleteModalRef = useRef();
  const [isLocationModal, setIsLocationModal] = useState(false);
  const [map, setMap] = useState();

  useEffect(() => {
    if (map && isLocationModal && location.placeId) {
      map.setCenter({
        lat: location.latitude,
        lng: location.longitude,
      });
    }
  }, [isLocationModal, map, location]);

  const getPlace = useCallback(
    (lat = 0, lng = 0) =>
      new window.google.maps.Geocoder().geocode(
        { location: { lat, lng }, language: "es" },
        ([place]) => {
          if (!place) {
            return;
          }
          autocompleteModalRef.current.value = place.formatted_address;
          onChange(
            formatLocationResult(place, place.formatted_address, location),
          );
        },
      ),
    [location, onChange],
  );

  const onGetCurrentLocation = useCallback(async () => {
    try {
      const position = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, () =>
          reject("No se puede obtener tu ubicación"),
        ),
      );
      const { latitude, longitude } = position.coords;
      getPlace(latitude, longitude);
    } catch (error) {
      status.updateErrorMessage(error);
    }
  }, [getPlace, status]);

  return (
    <Fragment>
      <tr>
        <Cell>
          {numeral > 1 && (
            <VStack height="inherit" width={10} alignItems="center">
              <Box
                width="2px"
                height="full"
                backgroundColor={colors.third}
                position="absolute"
                left="19px"
                bottom={0}
                alignSelf="center"
                zIndex={0}
              />
            </VStack>
          )}
        </Cell>
        <Cell>
          <HStack marginY={3} justifyContent="space-between" space={2}>
            <Heading color={colors.third} fontSize="lg" numberOfLines={1}>
              {title}
            </Heading>
            {onDelete && (
              <Pressable onPress={() => onDelete(location)}>
                <HStack>
                  <MdDeleteForever color={colors.primary} size={25} />
                  <Text bold color={colors.primary}>
                    Eliminar
                  </Text>
                </HStack>
              </Pressable>
            )}
          </HStack>
        </Cell>
      </tr>
      <tr>
        <Cell>
          <VStack height="inherit" width={10} alignItems="center">
            <Stack
              width={7}
              height={7}
              backgroundColor={colors.third}
              borderRadius={7}
              justifyContent="center"
              alignItems="center"
              zIndex={1}>
              <Text color={colors.white}>{numeral}</Text>
            </Stack>
            {!isLast && Boolean(location.duration) && (
              <Stack
                width={10}
                height={10}
                backgroundColor={colors.primary}
                borderRadius="50%"
                position="absolute"
                justifyContent="center"
                alignItems="center"
                bottom={0}
                zIndex={1}>
                <Text color={colors.white} textAlign="center" fontSize="10px">
                  {formatDuration(location.duration)}
                </Text>
              </Stack>
            )}
            {!isLast && (
              <Box
                width="2px"
                height="calc(100% - 6px)"
                backgroundColor={colors.third}
                position="absolute"
                bottom={0}
                alignSelf="center"
                zIndex={0}
              />
            )}
          </VStack>
        </Cell>
        <Cell>
          <button
            className={styles.button}
            onClick={() => setIsLocationModal(true)}>
            <HStack
              width="full"
              minH={50}
              borderColor={
                readOnly || disabled ? colors.transparent : colors.lightGray
              }
              borderWidth={1}
              borderRadius={5}
              overflow="hidden"
              paddingRight={location.name && !clearDisabled ? 0 : 3}
              paddingLeft={3}>
              <Image
                src={locationIcon}
                alt="location"
                width={15}
                height={15}
                marginRight={2}
              />
              <Text
                flex={1}
                paddingY={3}
                color={!location.name && "#757575"}
                textAlign="start">
                {location.name || "Dirección"}
              </Text>
              {Boolean(location.name) && !clearDisabled && (
                <Pressable
                  paddingY={3}
                  paddingX={2}
                  onPress={() =>
                    onChange({
                      ...location,
                      ...constants.location,
                      type: location.type,
                    })
                  }>
                  <MdOutlineClear size={15} />
                </Pressable>
              )}
            </HStack>
          </button>
        </Cell>
        <Cell>
          <CustomDatePicker
            selected={
              location.arrivalDate
                ? moment(location.arrivalDate).toDate()
                : null
            }
            onChange={(date) =>
              onChange({
                ...location,
                arrivalDate: moment(date).toISOString(),
              })
            }
            disabled={hideArrivalDate || !location.arrivalDate}
            readOnly
            minDays={minDays}
          />
        </Cell>
        <Cell>
          <CustomTimePicker
            selected={
              location.arrivalDate
                ? moment(location.arrivalDate).toDate()
                : null
            }
            onChange={(date) =>
              onChange({
                ...location,
                arrivalDate: moment(date).toISOString(),
              })
            }
            disabled={hideArrivalDate || !location.arrivalDate}
            readOnly
          />
        </Cell>
        <Cell>
          <Box
            borderColor={colors.transparent}
            borderWidth={1}
            borderRadius={5}
            minH={50}
            padding={3}
            opacity={
              !location.arrivalDate || !location.departureDate || hideStay
                ? 40
                : 100
            }>
            <Text numberOfLines={1}>
              {getStay(location.arrivalDate, location.departureDate, hideStay)}
            </Text>
          </Box>
        </Cell>
        <Cell>
          <CustomDatePicker
            selected={
              location.departureDate
                ? moment(location.departureDate).toDate()
                : null
            }
            onChange={(date) =>
              onChange({
                ...location,
                departureDate: moment(date).toISOString(),
              })
            }
            minDate={
              location.arrivalDate
                ? moment(location.arrivalDate).toDate()
                : null
            }
            disabled={
              (hideDepartureDate || !location.departureDate) &&
              !forceDepartureDate
            }
            readOnly={readOnlyDepartureDate}
            minDays={minDays}
          />
        </Cell>
        <Cell>
          <CustomTimePicker
            selected={
              location.departureDate
                ? moment(location.departureDate).toDate()
                : null
            }
            onChange={(date) =>
              location.departureDate &&
              onChange({
                ...location,
                departureDate: moment(date).toISOString(),
              })
            }
            minTime={
              location.arrivalDate &&
              moment(location.departureDate).format(moment.HTML5_FMT.DATE) ===
                moment(location.arrivalDate).format(moment.HTML5_FMT.DATE)
                ? moment(location.arrivalDate).toDate()
                : null
            }
            maxTime={
              location.arrivalDate &&
              moment(location.departureDate).format(moment.HTML5_FMT.DATE) ===
                moment(location.arrivalDate).format(moment.HTML5_FMT.DATE)
                ? moment(location.arrivalDate).endOf("day").toDate()
                : null
            }
            disabled={
              (hideDepartureDate || !location.departureDate) &&
              !forceDepartureDate
            }
            readOnly={readOnlyDepartureDate}
          />
        </Cell>
        <Cell>
          <textarea
            className={classNames(styles.textarea, {
              [styles.textareaDisabled]: readOnlyObservation,
            })}
            rows={4}
            placeholder={
              readOnlyObservation
                ? ""
                : "Ejemplo: frente a la farmacia Guadalajara"
            }
            value={location.observation}
            onChange={(e) =>
              onChange({
                ...location,
                observation: e.target.value,
              })
            }
            readOnly={readOnlyObservation}
          />
        </Cell>
      </tr>
      <Modal
        maxWidth={900}
        isOpen={isLocationModal}
        onRequestClose={() => setIsLocationModal(false)}>
        <VStack paddingX={5} paddingY={5} space={3}>
          <Heading fontSize="lg">Busca tu ubicación</Heading>
          <HStack
            width="full"
            minHeight={50}
            borderColor={colors.lightGray}
            borderWidth={1}
            borderRadius={5}
            overflow="hidden"
            space={2}
            paddingRight={3}>
            <Pressable
              paddingY={3}
              paddingLeft={3}
              onPress={() => setIsLocationModal(true)}>
              <Image src={locationIcon} alt="location" width={15} height={15} />
            </Pressable>
            <Autocomplete
              autoFocus
              ref={autocompleteModalRef}
              className={styles.input}
              apiKey={constants.googleMapsApiKey}
              placeholder="Dirección"
              defaultValue={location.name}
              options={{ types: [], componentRestrictions: { country: "mx" } }}
              language="es"
              onPlaceSelected={(place, inputRef) => {
                if (inputRef.value) {
                  onChange(
                    formatLocationResult(place, inputRef.value, location),
                  );
                }
              }}
              onChange={(e) => {
                if (!e.target.value) {
                  onChange({ ...constants.location, type: location.type });
                }
              }}
            />
            <button onClick={onGetCurrentLocation}>
              <MdMyLocation color={colors.secondary} size={20} />
            </button>
          </HStack>
          <Box height={400} width="full">
            <Map
              setRef={setMap}
              onClick={(event) =>
                getPlace(event.latLng.lat(), event.latLng.lng())
              }
              zoom={14}>
              {Boolean(location.placeId) && (
                <Marker
                  position={{ lat: location.latitude, lng: location.longitude }}
                  title={location.name}
                  draggable
                  onDragEnd={(event) =>
                    getPlace(event.latLng.lat(), event.latLng.lng())
                  }
                />
              )}
            </Map>
          </Box>
          <Pressable
            alignSelf="center"
            onPress={() => setIsLocationModal(false)}>
            <Box
              backgroundColor={colors.primary}
              paddingY={2}
              paddingX={50}
              borderRadius="full">
              <Text color={colors.white}>Guardar</Text>
            </Box>
          </Pressable>
        </VStack>
      </Modal>
    </Fragment>
  );
}

Location.propTypes = {
  location: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  title: PropTypes.string.isRequired,
  numeral: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLast: PropTypes.bool,
  hideArrivalDate: PropTypes.bool,
  hideDepartureDate: PropTypes.bool,
  hideStay: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnlyDepartureDate: PropTypes.bool,
  onDelete: PropTypes.func,
  clearDisabled: PropTypes.bool,
  readOnlyObservation: PropTypes.bool,
  forceDepartureDate: PropTypes.bool,
  minDays: PropTypes.number,
};

Location.defaultProps = {
  numeral: 1,
  isLast: false,
  hideArrivalDate: false,
  hideDepartureDate: false,
  hideStay: false,
  readOnly: false,
  disabled: false,
  readOnlyDepartureDate: false,
  clearDisabled: false,
  readOnlyObservation: false,
  forceDepartureDate: false,
  minDays: 3,
};

export default React.memo(Location);
